import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Bookmark, BookmarkCheck, Check, Circle, Highlighter, Trash2, NotebookPen, Save } from "lucide-react";

// Renders paragraph text with highlighted substrings wrapped in <mark>.
function renderWithHighlights(text, highlightTexts) {
  if (!highlightTexts || highlightTexts.length === 0) return text;
  // Sort longest first to avoid nested/partial issues
  const sorted = [...new Set(highlightTexts)].sort((a, b) => b.length - a.length);
  const parts = [{ t: text, mark: false }];
  for (const needle of sorted) {
    if (!needle) continue;
    const next = [];
    for (const seg of parts) {
      if (seg.mark || !seg.t.includes(needle)) { next.push(seg); continue; }
      const pieces = seg.t.split(needle);
      pieces.forEach((p, i) => {
        if (p) next.push({ t: p, mark: false });
        if (i < pieces.length - 1) next.push({ t: needle, mark: true });
      });
    }
    parts.splice(0, parts.length, ...next);
  }
  return parts.map((seg, i) =>
    seg.mark
      ? <mark key={i} className="bg-[#C5A059]/30 text-[#0A0A0A] px-0.5">{seg.t}</mark>
      : <span key={i}>{seg.t}</span>
  );
}

export default function Chapter() {
  const { moduleId, chapterId } = useParams();
  const [data, setData] = useState(null);
  const [scroll, setScroll] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [selection, setSelection] = useState(null); // { text, x, y }
  const articleRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/modules/${moduleId}/chapters/${chapterId}`).then((r) => setData(r.data)).catch(() => {});
    window.scrollTo({ top: 0 });
  }, [moduleId, chapterId]);

  useEffect(() => {
    if (!user || user === false) return;
    Promise.all([
      api.get("/bookmarks"),
      api.get("/progress"),
      api.get(`/annotations/${moduleId}/${chapterId}`),
    ]).then(([b, p, a]) => {
      setBookmarked(b.data.some((x) => x.module_id === moduleId && x.chapter_id === chapterId));
      setCompleted(p.data.completed.some((x) => x.module_id === moduleId && x.chapter_id === chapterId));
      setHighlights(a.data.highlights || []);
      setNote(a.data.note || "");
    }).catch(() => {});
    // Record activity (fire and forget)
    api.post("/activity", { module_id: moduleId, chapter_id: chapterId }).catch(() => {});
  }, [user, moduleId, chapterId]);

  const onScroll = useCallback(() => {
    const h = document.documentElement;
    const total = h.scrollHeight - h.clientHeight;
    setScroll(total > 0 ? Math.min(100, (h.scrollTop / total) * 100) : 0);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  // Selection listener for highlight toolbar
  const handleSelection = useCallback(() => {
    if (!articleRef.current) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) { setSelection(null); return; }
    const range = sel.getRangeAt(0);
    // Ensure selection is inside the article
    if (!articleRef.current.contains(range.commonAncestorContainer)) { setSelection(null); return; }
    const text = sel.toString().trim();
    if (text.length < 4 || text.length > 400) { setSelection(null); return; }
    const rect = range.getBoundingClientRect();
    setSelection({
      text,
      x: rect.left + rect.width / 2 + window.scrollX,
      y: rect.top + window.scrollY - 8,
    });
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("selectionchange", () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) setSelection(null);
    });
    return () => document.removeEventListener("mouseup", handleSelection);
  }, [handleSelection]);

  const highlightTexts = useMemo(() => highlights.map((h) => h.text), [highlights]);

  const saveHighlight = async () => {
    if (!selection) return;
    if (!user || user === false) { nav("/login"); return; }
    try {
      const { data: h } = await api.post("/highlights", { module_id: moduleId, chapter_id: chapterId, text: selection.text });
      setHighlights((prev) => [...prev, h]);
      window.getSelection()?.removeAllRanges();
      setSelection(null);
      toast.success("Highlight saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeHighlight = async (id) => {
    try {
      await api.delete(`/highlights/${id}`);
      setHighlights((prev) => prev.filter((h) => h.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveNote = async () => {
    if (!user || user === false) { nav("/login"); return; }
    setNoteSaving(true);
    try {
      await api.put("/notes", { module_id: moduleId, chapter_id: chapterId, text: note });
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNoteSaving(false);
    }
  };

  const toggleBookmark = async () => {
    if (!user || user === false) { nav("/login"); return; }
    try {
      if (bookmarked) {
        await api.delete("/bookmarks", { data: { module_id: moduleId, chapter_id: chapterId } });
        setBookmarked(false);
        toast.success("Bookmark removed");
      } else {
        await api.post("/bookmarks", { module_id: moduleId, chapter_id: chapterId });
        setBookmarked(true);
        toast.success("Bookmarked");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleComplete = async () => {
    if (!user || user === false) { nav("/login"); return; }
    try {
      if (completed) {
        await api.delete("/progress", { data: { module_id: moduleId, chapter_id: chapterId } });
        setCompleted(false);
      } else {
        await api.post("/progress", { module_id: moduleId, chapter_id: chapterId });
        setCompleted(true);
        toast.success("Chapter marked as read");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return <div className="max-w-3xl mx-auto p-12 text-[#525252]">Loading…</div>;
  const { module, chapter, prev, next } = data;

  return (
    <div>
      <div className="reading-progress-bar" style={{ width: `${scroll}%` }} data-testid="reading-progress" />

      <div className="max-w-3xl mx-auto px-6 py-14">
        <Link data-testid="chapter-back-to-module" to={`/modules/${module.id}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> {module.title}
        </Link>

        <div className="mb-10 border-b border-[#E5E5E0] pb-8">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4">
            Chapter {chapter.number}  ·  {chapter.reading_time} min read
          </div>
          <h1 data-testid="chapter-title" className="font-serif text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight leading-[1.05]">
            {chapter.title}
          </h1>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              data-testid="chapter-toggle-complete"
              onClick={toggleComplete}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-colors ${
                completed
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#FFFFFF]"
                  : "border-[#E5E5E0] text-[#0A0A0A] hover:border-[#0A0A0A]"
              }`}
            >
              {completed ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              {completed ? "Marked as read" : "Mark as read"}
            </button>
            <button
              data-testid="chapter-toggle-bookmark"
              onClick={toggleBookmark}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#E5E5E0] text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
            >
              {bookmarked ? <BookmarkCheck className="w-4 h-4 text-[#C5A059]" /> : <Bookmark className="w-4 h-4" />}
              {bookmarked ? "Bookmarked" : "Bookmark"}
            </button>
          </div>
          {user && user !== false && (
            <p className="mt-4 text-xs font-mono uppercase tracking-wider text-[#525252]">
              Tip · select any text to highlight it.
            </p>
          )}
        </div>

        <article ref={articleRef} data-testid="chapter-body" className="prose-article">
          {chapter.body.map((para, i) => (
            <p key={i}>{renderWithHighlights(para, highlightTexts)}</p>
          ))}
        </article>

        {/* Selection toolbar */}
        {selection && user && user !== false && (
          <button
            data-testid="highlight-selection-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={saveHighlight}
            style={{ position: "absolute", left: selection.x, top: selection.y, transform: "translate(-50%, -100%)" }}
            className="z-40 px-3 py-1.5 bg-[#0A0A0A] text-[#FFFFFF] text-xs font-medium flex items-center gap-1.5 shadow-lg btn-primary-hover"
          >
            <Highlighter className="w-3.5 h-3.5" /> Highlight
          </button>
        )}

        {/* Highlights list */}
        {user && user !== false && highlights.length > 0 && (
          <section className="mt-14" data-testid="chapter-highlights">
            <div className="flex items-center gap-2 mb-4">
              <Highlighter className="w-4 h-4 text-[#C5A059]" strokeWidth={1.75} />
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">Your highlights · {highlights.length}</div>
            </div>
            <div className="border-t border-[#E5E5E0]">
              {highlights.map((h) => (
                <div key={h.id} data-testid={`highlight-${h.id}`} className="flex items-start gap-3 py-4 border-b border-[#E5E5E0]">
                  <div className="w-1 self-stretch bg-[#C5A059]" />
                  <p className="flex-1 font-serif italic text-[#0A0A0A] leading-relaxed">{h.text}</p>
                  <button
                    data-testid={`highlight-remove-${h.id}`}
                    onClick={() => removeHighlight(h.id)}
                    className="p-1.5 text-[#525252] hover:text-[#0A0A0A] transition-colors"
                    aria-label="Remove highlight"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Personal note */}
        {user && user !== false && (
          <section className="mt-14" data-testid="chapter-note">
            <div className="flex items-center gap-2 mb-4">
              <NotebookPen className="w-4 h-4 text-[#C5A059]" strokeWidth={1.75} />
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">Your notes</div>
            </div>
            <textarea
              data-testid="chapter-note-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write your thoughts, questions, or key takeaways here — only you will see this."
              rows={6}
              className="w-full p-4 border border-[#E5E5E0] bg-[#FFFFFF] focus:border-[#0A0A0A] focus:outline-none text-[#0A0A0A] leading-relaxed font-sans resize-y"
            />
            <div className="mt-3 flex justify-end">
              <button
                data-testid="chapter-note-save"
                onClick={saveNote}
                disabled={noteSaving}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] disabled:opacity-50 transition-colors text-sm"
              >
                <Save className="w-3.5 h-3.5" /> {noteSaving ? "Saving..." : "Save note"}
              </button>
            </div>
          </section>
        )}

        <hr className="border-[#E5E5E0] my-12" />

        <div className="grid grid-cols-2 gap-4">
          <div>
            {prev && (
              <Link
                data-testid="chapter-prev"
                to={`/modules/${module.id}/chapters/${prev.id}`}
                className="block p-5 border border-[#E5E5E0] hover:border-[#0A0A0A] transition-colors group"
              >
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252] mb-1 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Previous · {prev.number}</div>
                <div className="font-serif font-semibold text-[#0A0A0A]">{prev.title}</div>
              </Link>
            )}
          </div>
          <div>
            {next && (
              <Link
                data-testid="chapter-next"
                to={`/modules/${module.id}/chapters/${next.id}`}
                className="block p-5 border border-[#E5E5E0] hover:border-[#0A0A0A] transition-colors text-right group"
              >
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252] mb-1 flex items-center justify-end gap-1">Next · {next.number} <ArrowRight className="w-3 h-3" /></div>
                <div className="font-serif font-semibold text-[#0A0A0A]">{next.title}</div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
